import { Link } from 'react-router-dom'
import { 
  GraduationCap, 
  MessageCircle, 
  Bell, 
  TrendingUp,
  Award,
  ArrowLeft,
  Star,
  Target,
  Gift,
  BookOpen,
  Calendar
} from 'lucide-react'

export default function StudentDemo() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Demo Banner */}
      <div className="bg-purple-600 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">Demo Mode</span>
            <span className="text-sm">You're viewing the Student dashboard</span>
          </div>
          <Link to="/" className="text-sm hover:underline flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">SchoolConnect</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-full">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="font-semibold text-yellow-700">450 pts</span>
              </div>
              <Bell className="w-5 h-5 text-gray-500" />
              <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">EJ</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Hi Emma! 👋</h1>
          <p className="text-gray-600">Ready to learn and earn more points today?</p>
        </div>

        {/* Points & Progress */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <Star className="w-8 h-8" />
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Level 5</span>
            </div>
            <p className="text-4xl font-bold">450</p>
            <p className="text-white/80">Total Points</p>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Next Level</span>
                <span>50 more pts</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full w-4/5 bg-white rounded-full" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">85%</p>
                <p className="text-sm text-gray-500">Average Grade</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-green-600 text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>+5% this month</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">12</p>
                <p className="text-sm text-gray-500">Badges Earned</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">⭐ Star</span>
              <span className="bg-teal-100 text-teal-700 px-2 py-1 rounded text-xs">🎯 Goal</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* My Badges */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">My Badges</h2>
              <div className="grid grid-cols-4 gap-4">
                {[
                  { icon: Star, label: 'Star Student', color: 'bg-yellow-100 text-yellow-600', earned: true },
                  { icon: Target, label: 'Goal Crusher', color: 'bg-teal-100 text-teal-600', earned: true },
                  { icon: Award, label: 'Helper', color: 'bg-blue-100 text-blue-600', earned: true },
                  { icon: Gift, label: 'Creative', color: 'bg-purple-100 text-purple-600', earned: false },
                ].map((badge, idx) => (
                  <div key={idx} className={`text-center p-4 rounded-xl ${badge.earned ? 'bg-gray-50' : 'bg-gray-100 opacity-50'}`}>
                    <div className={`w-12 h-12 ${badge.color} rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <badge.icon className="w-6 h-6" />
                    </div>
                    <p className="text-xs font-medium text-gray-700">{badge.label}</p>
                    {badge.earned && <span className="text-xs text-green-600">✓ Earned</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Achievements */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Achievements</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-yellow-50 rounded-xl">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Helper Badge Earned!</p>
                    <p className="text-sm text-gray-500">You helped a classmate with math</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-yellow-600">+50 pts</p>
                    <p className="text-xs text-gray-400">2 days ago</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Science Project A+</p>
                    <p className="text-sm text-gray-500">Great work on "The Solar System"</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-blue-600">+30 pts</p>
                    <p className="text-xs text-gray-400">3 days ago</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Math Test Improved!</p>
                    <p className="text-sm text-gray-500">75% → 92% Keep it up!</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">+20 pts</p>
                    <p className="text-xs text-gray-400">1 week ago</p>
                  </div>
                </div>
              </div>
            </div>

            {/* My Portfolio */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">My Portfolio</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="aspect-square bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-blue-600" />
                </div>
                <div className="aspect-square bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                  <Target className="w-8 h-8 text-green-600" />
                </div>
                <div className="aspect-square bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center border-2 border-dashed border-purple-300">
                  <span className="text-purple-600 text-2xl">+</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Next Goal */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Next Goal</h2>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                <Target className="w-8 h-8 mb-2" />
                <p className="font-semibold">Creative Badge</p>
                <p className="text-sm text-purple-100">Submit 3 art projects</p>
                <div className="mt-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Progress</span>
                    <span>2/3</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full w-2/3 bg-white rounded-full" />
                  </div>
                </div>
                <p className="text-xs text-purple-100 mt-2">+100 points when completed!</p>
              </div>
            </div>

            {/* Announcements */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Announcements</h2>
              <div className="space-y-3">
                <div className="p-3 bg-red-50 rounded-xl">
                  <p className="font-medium text-gray-900 text-sm">Field Trip Friday!</p>
                  <p className="text-xs text-gray-500">Don't forget permission slip</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <p className="font-medium text-gray-900 text-sm">Math Quiz</p>
                  <p className="text-xs text-gray-500">Chapter 5 on Friday</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl">
                  <p className="font-medium text-gray-900 text-sm">Sports Day</p>
                  <p className="text-xs text-gray-500">Next Tuesday - wear PE</p>
                </div>
              </div>
            </div>

            {/* Upcoming */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">This Week</h2>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-teal-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Science Project Due</p>
                    <p className="text-xs text-gray-500">Thursday</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Art Class</p>
                    <p className="text-xs text-gray-500">Wednesday</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-purple-900 py-12 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Make learning fun and rewarding!</h2>
          <p className="text-purple-100 mb-6">Join thousands of students earning points and badges</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register-school" className="bg-white text-purple-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors">
              Start 14-Day Free Trial
            </Link>
            <Link to="/demo/teacher" className="bg-white/10 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/20 transition-colors">
              See Teacher View
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
